

import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * Paginated product list with manual "Ver más productos" loading.
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid]
 * @property {HTMLElement} [viewMorePrevious]
 * @property {HTMLElement} [viewMoreNext]
 * @property {HTMLElement[]} [cards]
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  pages = new Map();
  infinityScrollObserver;
  #resolveNextPagePromise = null;
  #resolvePreviousPagePromise = null;
  #aspectRatioHelper;
  #loading = false;
  #generation = 0;

  connectedCallback() {
    super.connectedCallback();
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({ templateCard });
    }

    // Delegated click handler survives Shopify's partial DOM replacements.
    this.addEventListener('click', this.#handleClick);
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);

    this.#updateButton();
    this.#fetchPage('next');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
    this.infinityScrollObserver?.disconnect();
    this.#generation++;
  }

  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const next = target.closest('[ref="viewMoreNext"]');
    if (next && this.contains(next)) {
      event.preventDefault();
      this.#renderNextPage();
      return;
    }
    const previous = target.closest('[ref="viewMorePrevious"]');
    if (previous && this.contains(previous)) {
      event.preventDefault();
      this.#renderPreviousPage();
    }
  };

  #updateButton() {
    const button = this.querySelector('[ref="viewMoreNext"]');
    if (!button) return;
    const more = this.#shouldUsePage(this.#getPage('next'));
    const wrapper = button.closest('.baia-load-more');
    if (wrapper) wrapper.hidden = !more;
    button.hidden = !more;
    if ('disabled' in button) button.disabled = this.#loading;
    button.setAttribute('aria-busy', String(this.#loading));
    if (this.#loading) button.textContent = 'Cargando productos…';
    else button.textContent = 'Ver más productos';
  }

  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;
    const lastPage = Number(this.refs.grid?.dataset.lastPage);
    return Number.isFinite(lastPage) && lastPage > 0 && pageInfo.page >= 1 && pageInfo.page <= lastPage;
  }

  async #fetchPage(type) {
    const page = this.#getPage(type);
    if (!this.#shouldUsePage(page)) return;
    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } catch (error) {
      console.error('Baia: error al precargar productos', error);
    }
  }

  async #fetchSpecificPage(pageNumber, url) {
    const pageUrl = url || new URL(window.location.href);
    pageUrl.searchParams.set('page', String(pageNumber));
    pageUrl.hash = '';
    if (!this.#shouldUsePage({ page: pageNumber, url: pageUrl })) return;
    const html = await sectionRenderer.getSectionHTML(this.sectionId, true, pageUrl);
    this.pages.set(pageNumber, html);
  }

  async #renderNextPage() {
    if (this.#loading) return;
    const grid = this.refs.grid;
    if (!grid) return;
    const next = this.#getPage('next');
    if (!this.#shouldUsePage(next)) {
      this.#updateButton();
      return;
    }

    this.#loading = true;
    this.#updateButton();
    const generation = this.#generation;
    try {
      let items = this.#getGridForPage(next.page);
      if (!items) {
        await this.#fetchSpecificPage(next.page, next.url);
        items = this.#getGridForPage(next.page);
      }
      if (generation !== this.#generation || !this.isConnected || !items?.length) return;
      grid.append(...items);
      this.#aspectRatioHelper?.processNewElements();
      await yieldToMainThread();
      // Preserve the current URL and all previously displayed products.
      requestIdleCallback(() => this.#fetchPage('next'));
    } catch (error) {
      console.error('Baia: error al mostrar más productos', error);
    } finally {
      this.#loading = false;
      if (this.isConnected) this.#updateButton();
    }
  }

  async #renderPreviousPage() {
    if (this.#loading) return;
    const grid = this.refs.grid;
    const previous = this.#getPage('previous');
    if (!grid || !this.#shouldUsePage(previous)) return;
    this.#loading = true;
    const generation = this.#generation;
    try {
      let items = this.#getGridForPage(previous.page);
      if (!items) {
        await this.#fetchSpecificPage(previous.page, previous.url);
        items = this.#getGridForPage(previous.page);
      }
      if (generation !== this.#generation || !this.isConnected || !items?.length) return;
      const oldScroll = getScrollTop();
      const first = grid.firstElementChild;
      const oldTop = first ? first.getBoundingClientRect().top + oldScroll : 0;
      grid.prepend(...items);
      this.#aspectRatioHelper?.processNewElements();
      if (first) {
        const newTop = first.getBoundingClientRect().top + getScrollTop();
        scrollTo({ top: oldScroll + newTop - oldTop, behavior: 'instant' });
      }
      await yieldToMainThread();
    } catch (error) {
      console.error('Baia: error al cargar productos anteriores', error);
    } finally {
      this.#loading = false;
      if (this.isConnected) this.#updateButton();
    }
  }

  #getPage(type) {
    const grid = this.refs.grid;
    if (!grid) return;
    // Query live DOM instead of relying on refs.cards being refreshed after append().
    const cards = grid.querySelectorAll(':scope > [ref="cards[]"]');
    const card = type === 'previous' ? cards[0] : cards[cards.length - 1];
    if (!card) return;
    const current = Number(card.dataset.page);
    if (!Number.isFinite(current) || current < 1) return;
    const page = current + (type === 'previous' ? -1 : 1);
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.hash = '';
    return { page, url };
  }

  #getGridForPage(page) {
    const html = this.pages.get(page);
    if (!html) return;
    const document = new DOMParser().parseFromString(html, 'text/html');
    const grid = document.querySelector('[ref="grid"]');
    return grid?.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }

  #handleFilterUpdate = (event) => {
    const section = event.target?.closest?.('[id^="shopify-section-"]');
    if (section && section.id !== `shopify-section-${this.sectionId}`) return;
    this.#generation++;
    this.pages.clear();
    this.#loading = false;

    // Shopify replaces the grid asynchronously after a filter/sort change.
    const oldGrid = this.refs.grid;
    const observer = new MutationObserver(() => {
      const grid = this.refs.grid;
      if (!grid || !grid.querySelector('[ref="cards[]"]')) return;
      if (grid === oldGrid && grid.dataset.lastPage === oldGrid?.dataset.lastPage) return;
      observer.disconnect();
      if (!this.isConnected) return;
      this.#updateButton();
      this.#fetchPage('next');
    });
    observer.observe(this, { childList: true, subtree: true });
    setTimeout(() => {
      observer.disconnect();
      if (this.isConnected) {
        this.#updateButton();
        this.#fetchPage('next');
      }
    }, 1000);
  };
}
